// confirm-save-changes.service.ts
import { Injectable, inject } from '@angular/core';
import { ModalService } from './modal.service';
import { DocumentSaveService, ModifiedFieldService, SessionTestService, 
  OfflineStatusService } from '@formbird/services';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class ConfirmSaveChangesService {
  private modalService = inject(ModalService);
  private modifiedFieldService = inject(ModifiedFieldService);
  private documentSaveService = inject(DocumentSaveService);
  private router = inject(Router);
  private offlineStatusService = inject(OfflineStatusService);
  private sessionTestService = inject(SessionTestService);

  /**
   * Prompt the user with "Save Changes?" modal.
   * Automatically handles saving and navigation if needed.
   */
  async confirm(options: {
    onSuccess?: () => void | Promise<void>,
    onDiscard?: () => void,
    onCancel?: () => void,
    nextUrl?: string
  }): Promise<boolean> {
    if (!this.modifiedFieldService.isDirty()) {
      return true;
    }

    const saveFunc = async () => {
      const unsavedIds = this.modifiedFieldService.getAllChangedDocumentListIds();
      try {
        await Promise.all(unsavedIds.map(id => this.documentSaveService.save(id)));
        options.onSuccess?.();
        if (options.nextUrl) {
          this.router.navigate([options.nextUrl]);
        }
      } catch (err: any) {
        console.error(err);
        this.modalService.showDialog(err.message, 'ERROR');
      }
    };

    const discardFunc = () => {
      this.modifiedFieldService.resetFormDirty();
      options.onDiscard?.();
      if (options.nextUrl) {
        this.router.navigate([options.nextUrl]);
      }
    };

    const cancelFunc = () => {
      console.log('Cancelling due to unsaved changes');
      options.onCancel?.();
    };

    try {
      // Only check session if online
      if (this.offlineStatusService.isConnected()) { 
        // Check if user is logged in before showing the modal
        await this.sessionTestService.testSession();
      }

      this.modalService.showDialog(
        '<p class="save-changes-title">Save changes?</p><p class="save-changes-body">If you leave this page, your changes will be lost.</p>',
        'WARNING',
        saveFunc,
        cancelFunc,
        discardFunc,
        null,
        { confirmButtonText: 'Save' }
      );
    } catch (err) {
      // discard and reload to show login
      discardFunc();
      window.location.reload();
    }

    return false;
  }
}
